import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PenTool, Package, Bot, Clock, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface ActivityItem {
  id: string;
  icon: React.ReactNode;
  text: string;
  time: string;
  href: string;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "Yesterday";
  return `${days}d ago`;
}

export function RecentActivity() {
  const { user } = useAuth();
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActivity = async () => {
      if (!user) { setLoading(false); return; }

      const items: ActivityItem[] = [];

      // Fetch recent scripts
      const { data: scripts } = await supabase
        .from("nexis_projects")
        .select("id, title, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(3);

      scripts?.forEach((s) => {
        items.push({
          id: `script-${s.id}`,
          icon: <PenTool className="w-4 h-4 text-primary" />,
          text: `Created script: "${s.title}"`,
          time: s.created_at,
          href: "/app/nexis-ai",
        });
      });

      // Fetch recent monetization projects
      const { data: products } = await supabase
        .from("monetization_projects")
        .select("id, title, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(3);

      products?.forEach((p) => {
        items.push({
          id: `product-${p.id}`,
          icon: <Package className="w-4 h-4 text-green-500" />,
          text: `Generated product ideas for: "${p.title}"`,
          time: p.created_at,
          href: "/app/monetization-ai",
        });
      });

      // Fetch recent conversations
      const { data: convos } = await supabase
        .from("conversations")
        .select("id, title, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(3);

      convos?.forEach((c) => {
        items.push({
          id: `convo-${c.id}`,
          icon: <Bot className="w-4 h-4 text-blue-500" />,
          text: `Asked AskFint: "${c.title}"`,
          time: c.created_at,
          href: "/app/askfint",
        });
      });

      // Sort by time and take top 5
      items.sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());
      setActivities(items.slice(0, 5));
      setLoading(false);
    };

    fetchActivity();
  }, [user]);

  if (loading || activities.length === 0) return null;

  return (
    <div className="glass-card p-6">
      <div className="flex items-center gap-2 mb-4">
        <Clock className="w-5 h-5 text-muted-foreground" />
        <h2 className="font-display text-xl font-semibold">Recent Activity</h2>
      </div>
      <div className="space-y-3">
        {activities.map((item) => (
          <Link
            key={item.id}
            to={item.href}
            className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors group"
          >
            <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
              {item.icon}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-foreground truncate">{item.text}</p>
              <p className="text-xs text-muted-foreground">{timeAgo(item.time)}</p>
            </div>
            <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
          </Link>
        ))}
      </div>
    </div>
  );
}
